import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ImagePlus } from "lucide-react";
import { apiClient } from "../lib/api";
import UserAvatar from "./UserAvatar";

const MAX_BYTES = 2 * 1024 * 1024;
const MAX_DIM = 512;

/**
 * Avatar picker UI — preview + file input. Reads the file, resizes to <=512px
 * (saves backend storage), uploads via /auth/avatar (data-URL form).
 */
export default function AvatarUpload({ user, onUpdated }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();

  const downscale = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Prefer JPEG for photos; PNG kept for already-small PNGs.
        const mime = file.type === "image/png" && Math.max(w, h) <= 256 ? "image/png" : "image/jpeg";
        resolve(c.toDataURL(mime, 0.88));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("That image is too large (max 8MB)"); return; }
    setBusy(true);
    try {
      const dataUrl = await downscale(file);
      if (dataUrl.length > MAX_BYTES * 1.4) {
        toast.error("Could not compress avatar enough — try a smaller image");
        return;
      }
      await apiClient.post("/auth/avatar", { image: dataUrl });
      toast.success("Avatar updated");
      await onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update avatar");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await apiClient.post("/auth/avatar", { image: "" });
      toast.success("Reset to default avatar");
      await onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset avatar");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-4" data-testid="avatar-upload">
      <UserAvatar src={user?.avatar_url} size={84} glow />
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="sm-btn-primary px-4 py-2 flex items-center gap-2 disabled:opacity-50"
          data-testid="avatar-upload-btn"
        >
          {user?.avatar_url ? <Upload size={14} /> : <ImagePlus size={14} />}
          {busy ? "UPLOADING…" : (user?.avatar_url ? "CHANGE AVATAR" : "UPLOAD AVATAR")}
        </button>
        {user?.avatar_url && (
          <button
            type="button"
            onClick={clear}
            disabled={busy}
            className="sm-btn-outline px-4 py-2 flex items-center gap-2 text-xs"
            data-testid="avatar-reset-btn"
          >
            <Trash2 size={12} /> RESET TO DEFAULT
          </button>
        )}
        <div className="text-faint font-mono text-[10px] tracking-[0.25em] uppercase">PNG · JPG · ≤2MB</div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="avatar-upload-input" />
    </div>
  );
}
